import { useMemo } from 'react';
import type { JourneyCanvas } from '@/api/marketing.api';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';

type Props = {
  canvasText: string;
  onCanvasTextChange: (value: string) => void;
  onSave: () => void;
  saving?: boolean;
  readOnly?: boolean;
};

export function parseCanvasJson(text: string): JourneyCanvas | null {
  try {
    const parsed = JSON.parse(text) as JourneyCanvas;
    if (!Array.isArray(parsed.nodes) || !Array.isArray(parsed.edges)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function JourneyCanvasEditor({ canvasText, onCanvasTextChange, onSave, saving, readOnly }: Props) {
  const canvas = useMemo(() => parseCanvasJson(canvasText), [canvasText]);
  const parseError = canvasText.trim() && !canvas;

  return (
    <div className="space-y-4">
      <div>
        <Textarea
          label="Canvas JSON (nodes + edges)"
          className="font-mono text-xs min-h-[160px]"
          value={canvasText}
          onChange={(e) => onCanvasTextChange(e.target.value)}
          readOnly={readOnly}
          spellCheck={false}
        />
        {parseError && <p className="text-xs text-red-600 mt-1">Invalid JSON — must include nodes and edges arrays.</p>}
      </div>

      {canvas && (
        <div className="overflow-x-auto pb-2">
          <p className="text-xs text-slate-500 mb-2">Preview</p>
          <div className="flex items-center gap-2 min-w-max">
            {canvas.nodes.map((node, i) => (
              <div key={node.key} className="flex items-center gap-2">
                <div
                  className={`px-3 py-2 rounded-lg border-2 text-xs font-medium min-w-[88px] text-center ${
                    node.type === 'GOAL'
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
                      : node.type === 'DELAY'
                        ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                        : 'border-violet-200 bg-violet-50 text-violet-800 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300'
                  }`}
                >
                  <div className="text-[10px] text-slate-400">{node.type}</div>
                  {node.label}
                </div>
                {i < canvas.nodes.length - 1 && <span className="text-slate-300 dark:text-slate-600">→</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {!readOnly && (
        <Button loading={saving} onClick={onSave} disabled={!!parseError}>
          Save canvas
        </Button>
      )}
    </div>
  );
}
